"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import Accordion from "@/components/ui/accordion";
import { holdNavbar, releaseNavbar } from "@/components/layout/Header/useScrollDirection";

const DEFAULT_SELECTOR = "h2, h3";

const slugify = (text, fallback) => {
    const slug = (text || "")
        .trim()
        .replace(/\s+/g, "-")
        .replace(/[^\p{L}\p{N}_-]/gu, "")
        .slice(0, 60);

    return slug || fallback;
};

/**
 * فهرست مطالب خودکار.
 *
 * تمام هدینگ‌های داخل `rootRef` را پیدا می‌کند، برای هدینگ‌های بدون id
 * شناسه می‌سازد و آن‌ها را به صورت درختی (h2 و h3های زیرمجموعه‌اش) نشان می‌دهد.
 * خود فهرست و هر شاخه‌ی h2 با اکاردیون گلوبال پروژه باز و بسته می‌شوند.
 *
 * هدینگی که نباید در فهرست بیاید کافی است `data-toc-ignore` داشته باشد.
 */
export default function TableOfContents({
    rootRef,
    // جایگزین rootRef وقتی والد Server Component است و ref ندارد
    rootId,
    idPrefix = "toc",
    title = "فهرست مطالب",
    selector = DEFAULT_SELECTOR,
    defaultOpen = true,
    getScrollOffset,
    beforeNavigate,
    className = "",
}) {
    const [items, setItems] = useState([]);
    const [activeId, setActiveId] = useState(null);

    // نگه‌داشتن خود نودها به جای getElementById: صفحه‌ی سینگل محصول درخت
    // موبایل و دسکتاپ را هم‌زمان رندر می‌کند و id ها تکراری می‌شوند.
    const nodesRef = useRef(new Map());
    const observerRef = useRef(null);
    const observedRef = useRef(null);
    const frameRef = useRef(null);
    const isProgrammaticScroll = useRef(false);
    const scrollTimeout = useRef(null);
    const stopNavigation = useRef(null);

    const getRoot = useCallback(
        () => rootRef?.current || (rootId ? document.getElementById(rootId) : null),
        [rootRef, rootId]
    );

    const collect = useCallback(() => {
        const root = getRoot();
        if (!root) return;

        const headings = Array.from(root.querySelectorAll(selector)).filter(
            (node) => !node.hasAttribute("data-toc-ignore") && node.textContent.trim()
        );

        const nodes = new Map();

        const list = headings.map((node, index) => {
            const text = node.textContent.trim();
            const id = `${idPrefix}-${slugify(text, "heading")}-${index}`;

            if (node.id !== id) node.id = id;
            nodes.set(id, node);

            // هر چیزی که h2 نیست زیرمجموعه حساب می‌شود و با تورفتگی می‌آید
            return {
                id,
                text,
                index,
                is_sub: node.tagName.toLowerCase() !== "h2",
            };
        });

        nodesRef.current = nodes;

        setItems((prev) => {
            const is_same =
                prev.length === list.length &&
                prev.every((item, i) => item.id === list[i].id);

            return is_same ? prev : list;
        });
    }, [getRoot, selector, idPrefix]);

    /* بعد از هر رندر دوباره اسکن می‌کنیم: React می‌تواند محتوای
       dangerouslySetInnerHTML را با نودهای تازه جایگزین کند و آن‌وقت هم id ها
       می‌پرند و هم MutationObserver روی یک نود جدا‌شده می‌ماند. */
    useEffect(() => {
        const root = getRoot();
        if (!root) return;

        collect();

        if (observedRef.current === root || typeof MutationObserver === "undefined") return;

        observerRef.current?.disconnect();

        const observer = new MutationObserver(() => {
            cancelAnimationFrame(frameRef.current);
            frameRef.current = requestAnimationFrame(collect);
        });

        observer.observe(root, { childList: true, subtree: true });

        observerRef.current = observer;
        observedRef.current = root;
    });

    /* هایلایت کردن سرفصلی که کاربر روی آن است */
    useEffect(() => {
        if (!items.length || typeof IntersectionObserver === "undefined") return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (isProgrammaticScroll.current) return;

                const visible = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

                if (visible[0]?.target?.id) setActiveId(visible[0].target.id);
            },
            { rootMargin: "-20% 0px -60% 0px", threshold: 0.1 }
        );

        nodesRef.current.forEach((node) => {
            if (node.isConnected) observer.observe(node);
        });

        return () => observer.disconnect();
    }, [items]);

    useEffect(
        () => () => {
            stopNavigation.current?.();
            cancelAnimationFrame(frameRef.current);
            observerRef.current?.disconnect();
        },
        []
    );

    /* در طول اسکرول ردیف دوم هدر بسته نگه داشته می‌شود، پس مقصد را با ارتفاع
       هدر بسته حساب می‌کنیم؛ وگرنه آخر اسکرول یک تصحیح اضافه به پایین می‌خورد. */
    const resolveOffset = useCallback(() => {
        if (getScrollOffset) return getScrollOffset();

        const styles = getComputedStyle(document.documentElement);
        const header_height =
            parseFloat(styles.getPropertyValue("--header-height-collapsed")) ||
            parseFloat(styles.getPropertyValue("--header-height")) ||
            0;

        return header_height + 12;
    }, [getScrollOffset]);

    /* هدینگ را همیشه لحظه‌ی مصرف پیدا می‌کنیم؛ بین کلیک تا اسکرول ممکن است
       React محتوا را دوباره رندر کرده و نود قبلی از صفحه جدا شده باشد. */
    const resolveNode = useCallback(
        (item) => {
            const cached = nodesRef.current.get(item.id);
            if (cached?.isConnected) return cached;

            const root = getRoot();
            if (!root) return null;

            return root.querySelectorAll(selector)[item.index] || null;
        },
        [getRoot, selector]
    );

    const navigate = useCallback(
        (item) => {
            if (!resolveNode(item)) return;

            // اگر هدینگ داخل یک باکس جمع‌شده باشد، اول بازش می‌کنیم
            const did_expand = beforeNavigate?.(item) === true;

            setActiveId(item.id);
            stopNavigation.current?.();
            isProgrammaticScroll.current = true;
            holdNavbar();

            let stopped = false;
            let started_at = 0;
            let moved_at = 0;
            let last_y = 0;

            const onScroll = () => {
                moved_at = performance.now();
            };

            const finish = () => {
                if (stopped) return;
                stopped = true;
                clearTimeout(scrollTimeout.current);
                window.removeEventListener("scroll", onScroll);
                window.removeEventListener("wheel", finish);
                window.removeEventListener("touchstart", finish);
                stopNavigation.current = null;
                isProgrammaticScroll.current = false;
                releaseNavbar();
            };

            stopNavigation.current = finish;

            // اگر کاربر خودش وسط راه اسکرول کرد، کنترل را به او برمی‌گردانیم
            window.addEventListener("wheel", finish, { passive: true });
            window.addEventListener("touchstart", finish, { passive: true });
            window.addEventListener("scroll", onScroll, { passive: true });

            const scrollToNode = () => {
                const node = resolveNode(item);
                if (!node) return false;

                const top = window.scrollY + node.getBoundingClientRect().top - resolveOffset();
                const max_top = document.documentElement.scrollHeight - window.innerHeight;
                const target = Math.min(Math.max(top, 0), Math.max(max_top, 0));

                if (Math.abs(target - window.scrollY) <= 2) return false;

                window.scrollTo({ top: target, behavior: "smooth" });
                started_at = performance.now();
                moved_at = 0;
                last_y = window.scrollY;
                return true;
            };

            /* مدت اسکرول نرم مرورگر به طول مسیر بستگی دارد، پس به جای یک زمان ثابت
               صبر می‌کنیم تا واقعاً بایستد؛ اگر محتوا (عکس، Collapse) در این فاصله
               جابه‌جا شده بود، تصحیح را هم نرم انجام می‌دهیم نه با پرش. */
            const waitForStop = (attempt) => {
                if (stopped) return;

                // تا وقتی رویداد scroll می‌رسد در حرکت است؛ اگر هنوز شروع نشده،
                // کمی فرصت می‌دهیم تا انیمیشن مرورگر راه بیفتد
                const now = performance.now();
                if (window.scrollY !== last_y) {
                    moved_at = now;
                    last_y = window.scrollY;
                }

                const is_moving = moved_at
                    ? now - moved_at < 150
                    : now - started_at < 400;

                if (is_moving) {
                    scrollTimeout.current = setTimeout(() => waitForStop(attempt), 80);
                    return;
                }

                if (attempt < 3 && scrollToNode()) {
                    scrollTimeout.current = setTimeout(() => waitForStop(attempt + 1), 80);
                    return;
                }

                finish();
            };

            scrollTimeout.current = setTimeout(
                () => {
                    if (stopped) return;

                    if (!scrollToNode()) {
                        finish();
                        return;
                    }

                    scrollTimeout.current = setTimeout(() => waitForStop(0), 80);
                },
                did_expand ? 340 : 0
            );
        },
        [beforeNavigate, resolveNode, resolveOffset]
    );

    if (!items.length) return null;

    return (
        <Accordion
            className={`overflow-hidden rounded-xl bg-jmt-neutral-100 ${className}`}
            defaultOpen={defaultOpen}
            headerClassName="flex w-full items-center justify-between gap-3 px-4 py-4 lg:px-6 lg:py-5"
            title={<span className="text-sm font-bold text-jmt-main lg:text-base">{title}</span>}
            iconClassname="text-jmt-neutral-800"
        >
            <nav aria-label={title} className="px-4 pb-4 lg:px-6 lg:pb-6">
                <ul className="space-y-3 lg:space-y-4">
                    {items.map((item) => {
                        const is_active = activeId === item.id;

                        return (
                            <li key={item.id} className={item.is_sub ? "pr-5 lg:pr-6" : ""}>
                                <button
                                    type="button"
                                    onClick={() => navigate(item)}
                                    // حداقل ۲۴px تا هدف لمسی کافی باشد (Lighthouse target-size)
                                    className="flex min-h-6 w-full cursor-pointer items-center gap-2 text-right transition-colors"
                                >
                                    <span
                                        className={`shrink-0 rounded-full ${item.is_sub ? "h-[3px] w-[3px]" : "h-1 w-1"
                                            } ${is_active ? "bg-jmt-primary-900" : "bg-jmt-primary-800"}`}
                                    />
                                    <span
                                        className={`${item.is_sub ? "text-[13px] lg:text-sm" : "text-sm lg:text-base"
                                            } ${is_active
                                                ? "font-bold text-jmt-primary-900"
                                                : "text-jmt-primary-800 hover:text-jmt-primary-900"
                                            }`}
                                    >
                                        {item.text}
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </nav>
        </Accordion>
    );
}
